import { useWindowSize } from '@react-hook/window-size'
import {
  type Column,
  type ColumnFiltersState,
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel, type SortingState,
  type Table,
  useReactTable
} from '@tanstack/react-table'
import clsx from 'clsx'
import { ArrowLeftIcon, Filter } from 'lucide-react'
import React, { useEffect, useState } from 'react'

import { employersPaySchedules } from 'rails_routes'
import { Badge } from 'slab'
import { useGetPayrollPayPeriods } from 'utils/web_api'
import type { PayrollPayPeriod } from 'utils/web_api.schemas'

import DownloadDeductionsButton from './DownloadDeductionsButton'
import type { DeductionStatus, DeductionTableRow } from '../../types/deductions'
import type { PayPeriodStatus, PaySchedule, Provider } from '../../types/payroll'
import { getLocaleCurrencyString, getLocaleDateString } from '../../utils'
import { useGetConsistentDeductions } from '../hooks/useGetConsistentDeductions'
import { useGetPaySchedules } from '../hooks/useGetPaySchedules'
import {
  ADPRunInstructions,
  GustoInstructions,
  OtherInstructions,
  QuickbooksInstructions,
  RipplingInstructions,
} from '../PayrollProviderInstructions'
import { AccordionContent, AccordionTrigger, SingleItemAccordion } from '../reusable/Accordion'
import CopyButton from '../reusable/CopyButton'
import { SortableHeader } from '../reusable/table/SortableHeader'
import TanstackTable from '../reusable/table/TanstackTable'
import { TanstackTableGlobalSearch } from '../reusable/table/TanstackTableGlobalSearch'
import { Alert, AlertDescription, AlertTitle } from '../ui/alert'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '../ui/select'

type DeductionsPageProps = {
  instructionalImages: { [key: string]: { [key: string]: string } }
  logos: { [key: string]: string }
  payrollProvider: string
  coverageStartDates: string[],
  includeSsn: boolean,
  includeSourceId: boolean,
  showSecondarySourceIdAsColumn: boolean,
  hasPayrollAutomation: boolean,
  humanizedPayrollProvider: string,
  showPerPeriodDeductionsBanner: boolean,
  employerId: string,
  enableGeneratedReportDownload: boolean,
}

type DeductionsState = {
  deductions: DeductionTableRow[]
  latestNonPendingPeriod: string
}

const columnHelper = createColumnHelper<DeductionTableRow>()

const columns = [
  columnHelper.accessor('employeeId', {
    id: 'employeeId',
    header: 'Employee ID',
    cell: ({ row }) => {
      return row.getValue('employeeId')
    },
    enableGlobalFilter: true,
  }),
  columnHelper.accessor('familyName', {
    id: 'name',
    cell: ({ row }) => {
      const { givenName, familyName } = row.original
      return (
        <div className='body-bold'>
          {givenName} {familyName}
        </div>
      )
    },
    header: (info) => (
      <SortableHeader<DeductionTableRow>
        {...info}
        headerText='Name'
        className='min-w-20'
      />
    ),
    enableGlobalFilter: true,
  }),
  columnHelper.accessor('status', {
    id: 'status',
    cell: (info) => {
      const status = info.getValue()
      if (status === 'unchanged') return null

      let type: React.ComponentProps<typeof Badge>['type']
      let label = ''

      switch (status) {
        case 'created':
          type = 'success'
          label = 'New'
          break
        case 'updated':
          type = 'warning'
          label = 'Updated'
          break
        case 'corrected':
          type = 'thatch'
          label = 'Corrected'
          break
      }

      return (
        <Badge type={type}>
          {label}
        </Badge>
      )
    },
    header: ({ table, column }) => {
      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button aria-label='Filter status'>
              <div className='flex items-center space-x-1 hover:cursor-pointer'>
                <span>Status</span>
                <Filter size={16} />
              </div>
            </button>
          </DropdownMenuTrigger>

          <DropdownMenuContent align='end'>
            {[
              { value: 'created', label: 'New' },
              { value: 'updated', label: 'Updated' },
              { value: 'corrected', label: 'Corrected' },
              { value: 'unchanged', label: 'Unchanged' }
            ].map(({ value, label }) => (
              <DropdownMenuCheckboxItem
                key={value}
                checked={!!((column.getFilterValue() as string[]) || []).find(x => x === value)}
                onCheckedChange={() => toggleColumnStatus(column, value, table)}
              >
                {label}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )
    },
    filterFn: (row, columnId, filterValues) => {
      if (!filterValues || !filterValues.length) return true
      return filterValues.includes(row.original.status)
    },
    enableGlobalFilter: false,
  }),
  columnHelper.accessor('allowance', {
    id: 'allowance',
    cell: (info) => (
      <div className='text-right'>
        {getLocaleCurrencyString(info.getValue())}
      </div>
    ),
    header: (info) => (
      <div className='flex justify-end'>
        <div className='w-20'>
          <SortableHeader<DeductionTableRow>
            {...info}
            headerText='Allowance'
            align='right'
          />
        </div>
      </div>
    ),
    enableGlobalFilter: false,
  }),
  columnHelper.accessor('planCost', {
    id: 'planCost',
    cell: (info) => (
      <div className='text-right'>
        {getLocaleCurrencyString(info.getValue())}
      </div>
    ),
    header: (info) => (
      <div className='flex justify-end'>
        <div className='w-24'>
          <SortableHeader<DeductionTableRow>
            {...info}
            headerText='Plan Cost'
            align='right'
          />
        </div>
      </div>
    ),
    enableGlobalFilter: false,
  }),
  columnHelper.accessor('s125Amount', {
    id: 's125Amount',
    cell: (info) => (
      <div className='text-right w-48 justify-self-end'>
        <div className='flex items-center justify-end gap-1'>
          {getLocaleCurrencyString(info.getValue())}
          <CopyButton valueToCopy={info.getValue().toString()} />
        </div>
        <SingleItemAccordion>
          <AccordionTrigger className='hover:underline font-normal text-gray-500 py-1 justify-end text-right w-full'>
            <span className='mr-1 text-right'>Past deduction total</span>
          </AccordionTrigger>
          <AccordionContent className='body whitespace-nowrap'>
            <div className='text-right'>
              {(!info.row.original.coverageStartDate ||
                  new Date(info.row.original.coverageStartDate) >= new Date(info.row.original.periodPayDate))
                ? (
                    'No past deductions'
                  )
                : (
                  <>
                    {new Date(`${info.row.original.coverageStartDate}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} {' - '}
                    {new Date(new Date(`${info.row.original.periodPayDate}T00:00:00`).setDate(new Date(`${info.row.original.periodPayDate}T00:00:00`).getDate() - 1)).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}: {' '}
                    <span>{getLocaleCurrencyString(info.row.original.s125ExpectedYtd)}</span>
                  </>
                  )}
            </div>
          </AccordionContent>
        </SingleItemAccordion>
      </div>
    ),
    header: (info) => (
      <SortableHeader<DeductionTableRow>
        {...info}
        headerText='Pre-tax deduction'
        align='right'
      />
    ),
    enableGlobalFilter: false,
  }),
  columnHelper.accessor('posttaxAmount', {
    id: 'posttaxAmount',
    cell: (info) => (
      <div className='text-right w-48 justify-self-end'>
        <div className='flex items-center justify-end gap-1'>
          {getLocaleCurrencyString(info.getValue())}
          <CopyButton valueToCopy={info.getValue().toString()} />
        </div>
        <SingleItemAccordion>
          <AccordionTrigger className='hover:underline font-normal text-gray-500 py-1 justify-end text-right w-full'>
            <span className='mr-1 text-right'>Past deduction total</span>
          </AccordionTrigger>
          <AccordionContent className='body whitespace-nowrap'>
            <div className='text-right'>
              {(!info.row.original.coverageStartDate ||
                  new Date(info.row.original.coverageStartDate) >= new Date(info.row.original.periodPayDate))
                ? (
                    'No past deductions'
                  )
                : (
                  <>
                    {new Date(`${info.row.original.coverageStartDate}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} {' - '}
                    {new Date(new Date(`${info.row.original.periodPayDate}T00:00:00`).setDate(new Date(`${info.row.original.periodPayDate}T00:00:00`).getDate() - 1)).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}: {' '}
                    <span>{getLocaleCurrencyString(info.row.original.posttaxExpectedYtd)}</span>
                  </>
                  )}
            </div>
          </AccordionContent>
        </SingleItemAccordion>
      </div>
    ),
    header: (info) => (
      <SortableHeader<DeductionTableRow>
        {...info}
        headerText='Post-tax deduction'
        align='right'
      />
    ),
    enableGlobalFilter: false,
  }),
]

const getHighestPriorityStatus = (s125Status?: DeductionStatus, posttaxStatus?: DeductionStatus): DeductionStatus => {
  // If no statuses provided, return unchanged
  if (!s125Status && !posttaxStatus) return 'unchanged'

  // Check for highest priority first
  if (s125Status === 'corrected' || posttaxStatus === 'corrected') return 'corrected'
  if (s125Status === 'updated' || posttaxStatus === 'updated') return 'updated'
  if (s125Status === 'created' || posttaxStatus === 'created') return 'created'

  return 'unchanged'
}

function toggleColumnStatus (
  column: Column<DeductionTableRow, unknown>,
  selectedFilter: string,
  table: Table<DeductionTableRow>
) {
  const currentFilters = (column.getFilterValue() as string[]) || []
  const selectedFilterIndex = currentFilters.indexOf(selectedFilter)

  if (selectedFilterIndex < 0) {
    column.setFilterValue([...currentFilters, selectedFilter])
  } else {
    column.setFilterValue([
      ...currentFilters.slice(0, selectedFilterIndex),
      ...currentFilters.slice(selectedFilterIndex + 1),
    ])
  }

  // Reset to first page when filter changes
  table.setPageIndex(0)
}

type ProviderHash = {
  [key: string]: Provider;
}

// Add providers configuration
const providers: ProviderHash = {
  gusto: {
    id: 'gusto',
    name: 'Gusto',
    component: GustoInstructions,
  },
  rippling: {
    id: 'rippling',
    name: 'Rippling',
    component: RipplingInstructions,
  },
  adp_run: {
    id: 'adp_run',
    name: 'ADP Run',
    component: ADPRunInstructions,
  },
  quickbooks: {
    id: 'quickbooks',
    name: 'Quickbooks',
    component: QuickbooksInstructions,
  },
  other: {
    id: 'other',
    name: 'Other',
    component: OtherInstructions,
  },
}

const PayrollProviderButton = ({
  provider,
  setProvider,
  logoUrl,
}: {
  provider: Provider;
  setProvider: React.Dispatch<React.SetStateAction<string>>;
  logoUrl: string;
}) => {
  const [width] = useWindowSize()

  return (
    <button
      className={clsx(
        'flex flex-initial shadow-sm rounded-lg p-4 my-2 h-16 text-center align-middle cursor-pointer transition',
        'border border-gray-200 hover:border-thatch-500',
        'outline-none outline-offset-2 outline-2 focus-visible:outline-thatch-500'
      )}
      type='button'
      style={width > 768 ? { flex: '0 0 32%' } : { flex: '0 0 100%' }}
      onClick={() => setProvider(provider.id)}
    >
      <div className='justify-between content-center m-auto h-full'>
        <div className='h-full'>
          {logoUrl
            ? (
              <img src={logoUrl} className='h-full' />
              )
            : (
              <span style={{ verticalAlign: 'sub' }}>{provider.name}</span>
              )}
        </div>
      </div>
    </button>
  )
}

const PayrollProviderInstructions = ({
  selectedProvider,
  setProvider,
  logos,
  instructionalImages,
}: {
  selectedProvider: string;
  setProvider: React.Dispatch<React.SetStateAction<string>>;
  logos: { [key: string]: string };
  instructionalImages: { [key: string]: { [key: string]: string } };
}) => {
  if (selectedProvider) {
    const { component: ProviderInstructions, id } = providers[selectedProvider]

    return (
      <div className='bg-white p-6 border border-gray-200 rounded-2xl shadow-sm'>
        <div
          className='flex items-center text-thatch-purple-primary body cursor-pointer pb-4 w-48'
          onClick={() => setProvider('')}
        >
          <ArrowLeftIcon className='self-center' />
          <span className='pl-2'>Provider Selection</span>
        </div>
        <ProviderInstructions
          logoUrl={logos[id]}
          instructionalImages={instructionalImages[id]}
        />
      </div>
    )
  }

  return (
    <div className='w-full bg-white border border-gray-200 rounded-2xl p-6'>
      <div className='body mb-6'>
        Select your payroll provider from the list below to view one-time setup
        and deduction input instructions
      </div>
      <div id='provider-grid' className='flex justify-between flex-wrap'>
        {Object.values(providers).map((provider) => (
          <PayrollProviderButton
            key={provider.id}
            provider={provider}
            setProvider={setProvider}
            logoUrl={logos[provider.id]}
          />
        ))}
        {/* Leave extra blank space for grid */}
        <div style={{ flex: '0 0 32%' }} />
      </div>
    </div>
  )
}

const DeductionsPage = ({
  instructionalImages,
  logos,
  payrollProvider,
  includeSsn,
  includeSourceId,
  showSecondarySourceIdAsColumn,
  hasPayrollAutomation,
  humanizedPayrollProvider,
  coverageStartDates,
  showPerPeriodDeductionsBanner,
  employerId,
  enableGeneratedReportDownload,
}: DeductionsPageProps) => {
  const dates = coverageStartDates.map(date => new Date(`${date}T00:00:00`))
  const has2024AndFutureStartDates = dates.some(date => date.getFullYear() === 2024) &&
    dates.some(date => date.getFullYear() > 2024)

  const [state, setState] = useState<DeductionsState>({
    deductions: [],
    latestNonPendingPeriod: ''
  })

  const [payPeriods, setPayPeriods] = useState<PayrollPayPeriod[]>([])
  const [selectedPayPeriod, setSelectedPayPeriod] = useState<PayrollPayPeriod>()

  // Add sorting state
  const [sorting, setSorting] = useState<SortingState>([])

  // Add defaultSortingState and disableSortingForColumns
  const defaultSortingState: SortingState = []
  const disableSortingForColumns: { [key: string]: boolean } = {
    status: true,
    actions: true,
  }

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  })

  const [paySchedules, setPaySchedules] = useState<PaySchedule[]>([])
  const [selectedScheduleId, setSelectedScheduleId] = useState('')

  const { data: paySchedulesData } = useGetPaySchedules()

  const { data: payPeriodsData } = useGetPayrollPayPeriods({
    pay_schedule_id: selectedScheduleId
  })

  const { data: consistentDeductionsData } = useGetConsistentDeductions({
    payPeriod: selectedPayPeriod
      ? {
          id: selectedPayPeriod.id,
          end_date: selectedPayPeriod.end_date,
          pay_date: selectedPayPeriod.pay_date,
          deductions_available_on: selectedPayPeriod.deductions_available_on,
          status: selectedPayPeriod.status as PayPeriodStatus
        }
      : undefined,
  })

  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    []
  )

  // Add provider state with localStorage persistence
  const [provider, setProvider] = useState(() => {
    const saved = localStorage.getItem('provider')
    if (!saved) {
      return providers[payrollProvider]?.id || ''
    }
    const initialValue = JSON.parse(saved)
    return initialValue || ''
  })

  useEffect(() => {
    localStorage.setItem('provider', JSON.stringify(provider))
  }, [provider])

  // Memoize the selected schedule
  const selectedPaySchedule = React.useMemo(
    () => paySchedules.find((s) => s.id === selectedScheduleId),
    [paySchedules, selectedScheduleId]
  )

  useEffect(() => {
    // Fetch deductions when a period is selected
    if (selectedPayPeriod && selectedPaySchedule && consistentDeductionsData) {
      const consistentDeductions = consistentDeductionsData.data
      const rows = consistentDeductions.map((memberData) => {
        const payrollMetadata = memberData.payroll_metadata
        const payrollMetadataMap = new Map(Object.entries(payrollMetadata || {}))
        const secondarySourceId = showSecondarySourceIdAsColumn && payrollMetadataMap.has('secondary_source_id') ? payrollMetadataMap.get('secondary_source_id') : ''

        // The `payroll_metadata` field returned by the API is a map of key-value pairs. But we can't have dynamic column names in the CSV export,
        // so we transform it into a map of `metadataKeyX` and `metadataValueX` fields, with X being the index of the key-value pair. X is bounded
        // between 0 and 9 (`payroll_metadata` cannot have more than 10 keys).
        const metadataRow = payrollMetadata
          ? Object.entries(payrollMetadata).reduce<{ [key: string]: string; }>((acc, [key, value], idx) => {
            if (showSecondarySourceIdAsColumn && key === 'secondary_source_id') {
              return acc
            }

            acc[`metadataKey${idx}`] = key
            acc[`metadataValue${idx}`] = value
            return acc
          }, {})
          : {}

        return {
          // Default to empty string for the following three columns to ensure they aren't skipped by global search
          // (which skips columns where the first row value is null even if global filtering is enabled).
          employeeId: memberData.employee_id || '',
          givenName: memberData.given_name || '',
          familyName: memberData.family_name || '',
          fullName: `${memberData.given_name} ${memberData.family_name}`.toLowerCase(),
          s125Amount: parseFloat(memberData.s125_deduction.amount.value),
          posttaxAmount: parseFloat(memberData.posttax_deduction.amount.value),
          s125ExpectedYtd: parseFloat(memberData.s125_deduction.expected_ytd_amount.value),
          posttaxExpectedYtd: parseFloat(memberData.posttax_deduction.expected_ytd_amount.value),
          scheduleStartDate: selectedPaySchedule.start_date || '',
          periodPayDate: selectedPayPeriod.pay_date || '',
          s125Status: memberData.s125_deduction.status,
          posttaxStatus: memberData.posttax_deduction.status,
          status: getHighestPriorityStatus(
            memberData.s125_deduction.status,
            memberData.posttax_deduction.status
          ),
          allowance: parseFloat(memberData.s125_deduction.allowance_amount.value),
          planCost: parseFloat(memberData.s125_deduction.total_premiums.value) +
            parseFloat(memberData.posttax_deduction.total_premiums.value),
          sourceId: memberData.source_id,
          secondarySourceId,
          coverageStartDate: memberData.coverage_start_date,
          coverageEndDate: memberData.coverage_end_date,
          employerContribution: memberData.s125_deduction?.employer_contribution?.value
            ? parseFloat(memberData.s125_deduction.employer_contribution.value)
            : 0,
          ...metadataRow
        }
      })
      setState(prev => ({
        ...prev,
        deductions: rows as DeductionTableRow[]
      }))
    } else {
      setState(prev => ({
        ...prev,
        deductions: []
      }))
    }
  }, [selectedPayPeriod, selectedPaySchedule, consistentDeductionsData])

  // Pre-select first schedule when data loads
  useEffect(() => {
    if (paySchedulesData) {
      const paySchedules = paySchedulesData.data.filter(paySchedule => !paySchedule.archived && paySchedule.is_verified)
      setPaySchedules(paySchedules)
      if (paySchedules.length && !selectedScheduleId) {
        setSelectedScheduleId(paySchedules[0].id)
      }
    }
  }, [paySchedulesData])

  useEffect(() => {
    if (selectedScheduleId && payPeriodsData?.data) {
      setPayPeriods(payPeriodsData.data)
    } else {
      setPayPeriods([])
    }
  }, [selectedScheduleId, payPeriodsData])

  // Pre-select latest non-pending pay period when periods load, or fall back to first period in list
  useEffect(() => {
    if (payPeriods && payPeriods.length > 0 && !selectedPayPeriod) {
      // First try to find the latest non-pending period
      const latestNonPending = payPeriods
        .filter(period => period.status !== 'pending')
        .sort((a, b) => new Date(b.end_date).getTime() - new Date(a.end_date).getTime())[0]

      if (latestNonPending) {
        setSelectedPayPeriod(latestNonPending)
        setState(prev => ({
          ...prev,

          latestNonPendingPeriod: latestNonPending.id
        }))
      } else {
        // otherwise, grab the first available pay period
        setSelectedPayPeriod(payPeriods[0])
        setState(prev => ({
          ...prev,
          latestNonPendingPeriod: ''
        }))
      }
    }
  }, [payPeriods])

  const table = useReactTable({
    data: state.deductions,
    columns,
    getCoreRowModel: getCoreRowModel(),
    state: {
      sorting,
      pagination,
      columnFilters,
      columnVisibility: {
        // Include employeeId as a column to allow for filtering, but don't display it in the table
        employeeId: false,
      }
    },
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    meta: {
      setSorting,
      defaultSortingState,
      disableSortingForColumns,
    },
    globalFilterFn: (rows, columnId, filterValue) => {
      const columnValue = (columnId === 'name') ? rows.original.fullName : rows.getValue(columnId)
      if (typeof columnValue === 'string') {
        if (columnId === 'employeeId') {
          // Do an exact match for employee ID
          return columnValue === filterValue
        }
        return columnValue.toLowerCase().includes(filterValue.toLowerCase())
      }
      return false
    }
  })

  const isPendingPeriod = selectedPayPeriod?.status === 'pending'
  const isLatestNonPendingPeriod = selectedPayPeriod?.id === state.latestNonPendingPeriod

  // Modified schedule selection handler
  const handleScheduleSelect = (value: string) => {
    // Update all related state at once
    setSelectedScheduleId(value)
    setSelectedPayPeriod(undefined)
    setState(prev => ({
      ...prev,
      deductions: [],
      latestNonPendingPeriod: ''
    }))
  }

  // Add handler for period selection
  const handlePeriodSelect = (value: string) => {
    const selected = payPeriods.find(p => p.id === value)
    setSelectedPayPeriod(selected)
  }

  return (
    <div>
      <div className='max-w-96 mb-4'>
        <TanstackTableGlobalSearch
          table={table}
          placeholder='Search by employee name...'
        />
      </div>
      <div className='flex flex-wrap justify-between gap-4 mb-4'>
        <div className='flex flex-wrap items-center gap-4'>
          <div className='flex items-center content-center gap-2'>
            {/* Pay Schedule Dropdown */}
            <label>
              <span className='label-default font-medium text-ds-text-secondary mb-2'>
                Pay Schedule
              </span>
              <div className='flex flex-row border rounded-md'>
                <Select value={selectedScheduleId} onValueChange={handleScheduleSelect} disabled={paySchedules.length === 0}>
                  <SelectTrigger className='w-56 border-none'>
                    {selectedPaySchedule ? selectedPaySchedule.name : 'Pay schedule'}
                  </SelectTrigger>
                  <SelectContent>
                    {paySchedules.map((schedule) => (
                      <SelectItem key={schedule.id} value={schedule.id}>
                        {schedule.name}
                      </SelectItem>
                    ))}
                    <>
                      <div className='h-px bg-gray-200 my-2' />
                      <a href={employersPaySchedules.index()} className='relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 body outline-none focus:bg-gray-100 focus:text-gray-900 data-[disabled]:pointer-events-none data-[disabled]:opacity-50 dark:focus:bg-gray-800 dark:focus:text-gray-50 hover:cursor-pointer'>
                        Manage pay schedules
                      </a>
                    </>
                  </SelectContent>
                </Select>
              </div>
            </label>
          </div>

          {/* Pay Period Dropdown */}
          {/* TODO: Could use some deduplication */}
          <label>
            <span className='label-default font-medium text-ds-text-secondary mb-2'>
              Pay Period
            </span>
            <Select value={selectedPayPeriod?.id || ''} onValueChange={handlePeriodSelect}>
              <SelectTrigger className='flex flex-row justify-start gap-x-1 w-fit' disabled={payPeriods.length === 0}>
                {selectedPayPeriod
                  ? (
                    <span>
                      {new Date(`${selectedPayPeriod.pay_date}T00:00:00`).toLocaleDateString('en-US', {
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                      {selectedPayPeriod.status === 'pending'
                        ? (
                          <span className='ml-1 body text-muted-foreground'>(Upcoming)</span>
                          )
                        : isLatestNonPendingPeriod && (
                          <span className='ml-1 body text-muted-foreground'>(Latest)</span>
                        )}
                    </span>
                    )
                  : (
                      'Select a pay period'
                    )}
              </SelectTrigger>
              <SelectContent>
                {payPeriods.map((period) => (
                  <SelectItem key={period.id} value={period.id}>
                    {new Date(`${period.pay_date}T00:00:00`).toLocaleDateString('en-US', {
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                    {period.status === 'pending'
                      ? (
                        <span className='ml-1 body text-muted-foreground'>(Upcoming)</span>
                        )
                      : period.id === state.latestNonPendingPeriod && (
                        <span className='ml-1 body text-muted-foreground'>(Latest)</span>
                      )}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
        </div>

        <div className='flex mt-auto'>
          <DownloadDeductionsButton deductions={state.deductions} selectedPaySchedule={selectedPaySchedule} selectedPeriodData={selectedPayPeriod} includeSsn={includeSsn} includeSourceId={includeSourceId} showSecondarySourceIdAsColumn={showSecondarySourceIdAsColumn} employerId={employerId} payScheduleId={selectedScheduleId} enableGeneratedReportDownload={enableGeneratedReportDownload} />
        </div>
      </div>

      {/* This is a temporary banner to notify specific employers that their deductions are no longer monthly. We can remove it after February. */}
      {!hasPayrollAutomation && selectedPaySchedule?.source === 'finch' && has2024AndFutureStartDates && showPerPeriodDeductionsBanner && (
        <div className='mb-4'>
          <Alert>
            <AlertTitle>Your deductions are now per-period</AlertTitle>
            <AlertDescription>
              Thatch synced your pay schedule from {humanizedPayrollProvider}, and calculated deduction amounts to fit your pay schedule. You can upload these deductions without modification.
            </AlertDescription>
          </Alert>
        </div>
      )}

      {hasPayrollAutomation && selectedPaySchedule?.source === 'finch' && (
        <div className='mb-4'>
          <Alert className='border-green-500 bg-green-50/50'>
            <AlertTitle>Your deductions are automated</AlertTitle>
            <AlertDescription>
              Thatch will automatically submit new and updated deductions to {humanizedPayrollProvider}. No action is required from you. The information below is for your reference only.
            </AlertDescription>
          </Alert>
        </div>
      )}

      {isPendingPeriod && selectedPayPeriod && (
        <div className='mb-4'>
          <Alert>
            <AlertTitle>We're working on your deductions</AlertTitle>
            <AlertDescription>
              Your deductions for this period are not ready yet. They will be available on{' '}
              <span className='font-semibold'>{getLocaleDateString(new Date(`${selectedPayPeriod.deductions_available_on}T00:00:00`))}</span> for the upcoming{' '}
              <span className='font-semibold'>{getLocaleDateString(new Date(`${selectedPayPeriod.pay_date}T00:00:00`))}</span> pay date.
            </AlertDescription>
          </Alert>
        </div>
      )}

      {/* Only show table if we don't have a pending period */}
      {!isPendingPeriod && (
        <TanstackTable
          tableClasses='overflow-hidden'
          table={table}
          tableMessageHeightClass='h-16'
          paginationLabelSingular='deduction'
        />
      )}

      <div className='mt-6'>
        <h3 className='heading-sm mb-3'>Payroll Deductions Instructions</h3>
        <PayrollProviderInstructions
          selectedProvider={provider}
          setProvider={setProvider}
          logos={logos}
          instructionalImages={instructionalImages}
        />
      </div>
    </div>
  )
}

export default DeductionsPage
